import { useState } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, MapPin, ChevronDown, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { submitContact } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const schema = z.object({
  name: z.string().min(2, "Name is too short"),
  email: z.string().email("Enter a valid email"),
  phone: z.string().optional(),
  company: z.string().optional(),
  role: z.string().optional(),
  interest: z.string().optional(),
  message: z.string().min(10, "Please provide a bit more detail"),
  website: z.string().optional(), // honeypot
});

const FAQ = [
  { q: "How quickly can you engage on an incident?", a: "Retainer clients get a bridge within 30 minutes, 24/7. New engagements — a working session within 48 hours." },
  { q: "Do you sign real SLAs on MTTD and MTTC?", a: "Yes. Signed SLAs are core to how we operate — MTTD under 15 minutes for critical alerts, MTTC under 60 minutes for endpoint events." },
  { q: "Which SIEM / EDR platforms do you work across?", a: "Splunk, Sentinel, Chronicle, QRadar, Elastic — plus CrowdStrike, SentinelOne, Cortex XDR, and Defender for Endpoint." },
  { q: "Do you support Indian regulatory reporting (RBI, SEBI, CERT-In)?", a: "Yes — we file and support RBI, SEBI CSCRF, and CERT-In notifications as part of MDR and IR engagements." },
  { q: "Can you engage on a fixed-scope pilot?", a: "Yes. Most clients start with a 4-6 week scoped pilot — assessment, quick wins, and a target-state plan." },
];

const OFFICES = [
  { city: "Delhi NCR", address: "Corporate HQ · Gurugram, Haryana", phone: "+91 124 XXX XXXX" },
  { city: "Bengaluru", address: "Delivery & Engineering", phone: "+91 80 XXXX XXXX" },
  { city: "Dubai", address: "MEA Regional Presence", phone: "+971 4 XXX XXXX" },
];

export default function Contact() {
  const [success, setSuccess] = useState(false);
  const [openFaq, setOpenFaq] = useState(-1);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { website: "" } });

  useSEO({
    title: "Contact RockLadder Technologies",
    description: "Talk to a senior engineer. Book a consultation for MSSP, MDR, VAPT, IAM, cloud, or SASE engagements.",
    canonical: "https://rockladder.com/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  });

  const onSubmit = async (data) => {
    try {
      await submitContact({ ...data, source_page: "/contact" });
      setSuccess(true);
      reset();
      toast.success("Message received. A senior engineer will respond within one business day.");
    } catch {
      toast.error("Submission failed — please email hello@rockladder.com");
    }
  };

  return (
    <>
      <PageHero
        eyebrow="CONTACT"
        title="Talk to a senior"
        gradient="engineer."
        subtitle="No scripted intake calls. No gatekeeping. Get a working point of view on your program in 30 minutes."
        breadcrumb={[{ label: "Contact" }]}
      />

      <section className="relative py-12">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
          {/* Form */}
          <div className="relative glass-panel rounded-2xl p-6 md:p-10 overflow-hidden">
            <AnimatePresence mode="wait">
              {success ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="relative text-center py-12"
                >
                  {/* Simple gold particle burst */}
                  <div className="pointer-events-none absolute inset-0">
                    {Array.from({ length: 18 }).map((_, i) => (
                      <motion.span
                        key={i}
                        initial={{ opacity: 0, x: 0, y: 0 }}
                        animate={{
                          opacity: [0, 1, 0],
                          x: Math.cos((i / 18) * Math.PI * 2) * 140,
                          y: Math.sin((i / 18) * Math.PI * 2) * 140,
                        }}
                        transition={{ duration: 1.4, delay: i * 0.03, ease: "easeOut" }}
                        className="absolute top-1/2 left-1/2 h-1.5 w-1.5 rounded-full bg-gold"
                      />
                    ))}
                  </div>
                  <CheckCircle2 className="h-10 w-10 text-gold mx-auto" strokeWidth={1.6} />
                  <div className="mt-4 font-display text-2xl text-inkText-primary">Message received.</div>
                  <p className="mt-2 text-sm text-inkText-muted">A senior engineer will respond within one business day.</p>
                  <button
                    onClick={() => setSuccess(false)}
                    data-testid="contact-send-another"
                    className="mt-6 inline-flex items-center gap-1 text-xs text-gold-body hover:text-gold font-mono tracking-widest"
                  >
                    SEND ANOTHER →
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit(onSubmit)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  data-testid="contact-form"
                  className="grid grid-cols-1 md:grid-cols-2 gap-4"
                >
                  <input type="text" tabIndex={-1} autoComplete="off" {...register("website")} className="hidden" aria-hidden="true" />
                  {[
                    { k: "name", label: "Full name *", md: false },
                    { k: "email", label: "Work email *", md: false },
                    { k: "phone", label: "Phone", md: false },
                    { k: "company", label: "Company", md: false },
                    { k: "role", label: "Your role", md: false },
                    { k: "interest", label: "Area of interest", md: false },
                  ].map((f) => (
                    <label key={f.k} className="block">
                      <span className="block font-mono text-[10px] tracking-[0.24em] text-inkText-muted mb-1.5">{f.label.toUpperCase()}</span>
                      <input
                        {...register(f.k)}
                        data-testid={`contact-${f.k}`}
                        className="w-full rounded-lg bg-ink/60 border border-gold/20 px-3 py-2.5 text-sm text-inkText-primary focus:outline-none focus:border-gold"
                      />
                      {errors[f.k] && <span className="mt-1 block text-xs text-[#F5566B]">{errors[f.k].message}</span>}
                    </label>
                  ))}
                  <label className="block md:col-span-2">
                    <span className="block font-mono text-[10px] tracking-[0.24em] text-inkText-muted mb-1.5">MESSAGE *</span>
                    <textarea
                      rows={5}
                      {...register("message")}
                      data-testid="contact-message"
                      className="w-full rounded-lg bg-ink/60 border border-gold/20 px-3 py-2.5 text-sm text-inkText-primary focus:outline-none focus:border-gold"
                    />
                    {errors.message && <span className="mt-1 block text-xs text-[#F5566B]">{errors.message.message}</span>}
                  </label>
                  <div className="md:col-span-2 flex items-center justify-end">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="contact-submit"
                      className="rounded-full bg-gradient-to-b from-[#FFD37A] to-[#C89026] text-ink px-6 py-2.5 text-sm font-medium border border-gold/60 disabled:opacity-50"
                    >
                      {isSubmitting ? "Sending…" : "Send message"}
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

          {/* Contact aside */}
          <aside className="space-y-5">
            <div className="glass-panel rounded-2xl p-5">
              <div className="font-mono text-[10px] tracking-[0.24em] text-gold-body mb-3">DIRECT</div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-3 text-inkText-primary/90">
                  <Mail className="h-4 w-4 text-gold" />
                  <a href="mailto:hello@rockladder.com" className="hover:text-gold">hello@rockladder.com</a>
                </li>
                <li className="flex items-center gap-3 text-inkText-primary/90">
                  <Phone className="h-4 w-4 text-gold" />
                  <a href="tel:+911240000000" className="hover:text-gold">+91 124 XXX XXXX</a>
                </li>
              </ul>
            </div>
            <div className="glass-panel rounded-2xl p-5">
              <div className="font-mono text-[10px] tracking-[0.24em] text-gold-body mb-3">SOC 24/7 HOTLINE</div>
              <div className="flex items-center gap-3 text-sm text-inkText-primary/90">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-gold" />
                </span>
                <a href="tel:+911240000001" className="hover:text-gold">+91 124 XXX XXXX</a>
              </div>
              <p className="text-xs text-inkText-muted mt-2">For active incidents, retainer clients only.</p>
            </div>
          </aside>
        </div>
      </section>

      {/* Offices */}
      <section className="relative py-16">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-8">Offices</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {OFFICES.map((o) => (
              <div key={o.city} className="glass-panel rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gold/25 bg-ink-panel/60 text-gold">
                    <MapPin className="h-4 w-4" strokeWidth={1.8} />
                  </span>
                  <div className="font-display text-lg text-inkText-primary">{o.city}</div>
                </div>
                <p className="text-sm text-inkText-muted">{o.address}</p>
                <p className="text-xs text-inkText-muted mt-2 font-mono tracking-wider">{o.phone}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-8">FAQ</h2>
          <ul className="space-y-3">
            {FAQ.map((it, i) => (
              <li key={it.q} className="glass-panel rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="text-sm md:text-base text-inkText-primary">{it.q}</span>
                  <ChevronDown className={`h-4 w-4 text-gold transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-4 text-sm text-inkText-muted leading-relaxed">{it.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}
